/**
 * Remove duplicate (and empty) polygons from an ignore-zone `coordinates` array.
 *
 * Why: the polygon editor reassembles a zone's polygons at save time as
 * `[...shapes, activePolygon]` (IgnoreZoneBuilder.handleSaveEdits). When an
 * *existing* polygon is edited, PolygonDrawer's edit branch leaves that polygon
 * in BOTH `shapes` and `iZpolygonPoints`, so the concat lists it twice and the
 * zone is written to the API with the same polygon doubled. Customers saw this
 * as "doubled up" masking (BT-1092: two identical Vehicle polygons stacked).
 *
 * Two polygons are considered identical when their vertex lists match after
 * rounding each coordinate to 4 decimals (tolerates float noise; safe for both
 * pixel-space and normalized 0-1 coordinates). Identical polygons are
 * functionally redundant in a zone, so dropping the extras never changes intent.
 */
export const dedupePolygons = (coordinates: number[][][]): number[][][] => {
  if (!Array.isArray(coordinates)) {
    return coordinates;
  }
  const seen = new Set<string>();
  const result: number[][][] = [];
  for (const polygon of coordinates) {
    if (!Array.isArray(polygon) || polygon.length === 0) {
      continue;
    }
    const key = JSON.stringify(
      polygon.map((point) =>
        Array.isArray(point)
          ? point.map((n) => Math.round(n * 1e4) / 1e4)
          : point
      )
    );
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(polygon);
  }
  return result;
};
